import { defineBoot } from '#q-app/wrappers'
import { Screen } from 'quasar'
import { createClient } from 'contentful'
import { get, isEmpty } from 'lodash-es'
import { parseResponseEntry } from '@/utils/contentful.util'
import { $logger } from '@/boot/logger'
import { $prism } from '@/boot/prism'
import { $rollbar } from '@/boot/rollbar'

const environment = import.meta.env.VITE_CONTENTFUL_ENVIRONMENT
const space = import.meta.env.VITE_CONTENTFUL_SPACE
const host = import.meta.env.VITE_CONTENTFUL_PREVIEW_HOST
const clients = {
  // API to fetch content, published only
  Delivery: createClient({
    accessToken: import.meta.env.VITE_CONTENTFUL_DELIVERY_TOKEN,
    space,
    environment,
  }),
  // API to fetch content, published and preview
  DeliveryPreview: createClient({
    accessToken: import.meta.env.VITE_CONTENTFUL_PREVIEW_TOKEN,
    space,
    environment,
    host,
  }),
}

let preview = false
let client = clients.Delivery

export let $contentful = {}

export default defineBoot(async ({ router }) => {
  let route = router.currentRoute.value

  /**
   * Check for preview param in URL and set client accordingly
   */
  router.beforeEach(to => {
    preview = Boolean(localStorage.getItem('rrc_preview') || to.query?.preview || /^\/preview\/.+$/.test(to.path))

    if (preview) {
      client = clients.DeliveryPreview
    }

    route = to

    return
  })

  /**
   * Handle for spa-routing on [data-spa-route] link clicks
   */
  document.addEventListener(
    'click',
    event => {
      const target = event.target.closest('[data-spa-route]')
      if (target) {
        const href = target.getAttribute('href')
        event.preventDefault()
        router.push(href)
      }
    },
    true // Add the event listener in the capture phase instead of the bubble phase to handle for stopPropagation()
  )

  /**
   * Replace absolute site host URLs with relative URLs for SSG crawling
   * @param {string} content The content to replace URLs in
   * @returns {string} The content with URLs replaced
   */
  function replaceUrls(content) {
    return (
      content
        // force https protocol
        .replace(/src="(\/\/|http:\/\/)/g, 'src="https://')
        .replace(/^(\/\/|http:\/\/)/g, 'https://')
    )
  }

  /**
   * Replace tokens in content with session-specific data
   * @param {*} content The content to replace tokens with
   * @param {array} findReplace The find/replace strings to apply to content
   * @returns  The content with tokens replaced
   */
  function replaceContentTokens(content, findReplace = []) {
    const appConfigStore = useAppConfigStore()
    const { supportConfig } = storeToRefs(appConfigStore)
    const userStore = useUserStore()
    const { user } = storeToRefs(userStore)

    // ignore non-strings and strings without tokens
    if (!/\{\{.*?\}\}/.test(content)) {
      return content
    }

    // replace all tokens with empty string on server
    if (process.env.SERVER) {
      return content.replace(/\{\{.*?\}\}/g, '')
    }

    // const { userLocale, storeInfo, siteSettings } = ctx.store.state
    // const { market: marketHours = '', pickup: pickupHours = '' } = get(storeInfo, 'store.hours', {})

    // apply custom find/replace provided for this entry
    findReplace.forEach(entry => {
      let [find, replace] = entry.split('|')
      content = content.replaceAll(find, replace)
    })

    // session-specific tokens and attributes
    content = content
      // brand name
      .replaceAll('{{brandName}}', route.params.brandName || '')
      // city
      // .replace(/\{\{city\}\}/g, userLocale.market?.city || '')
      // customer service email
      .replace(/\{\{customerSupportEmail\}\}/g, supportConfig.value?.marketplace?.email)
      // customer service phone
      .replace(/\{\{customerSupportPhone\}\}/g, supportConfig.value?.marketplace?.phone)
      // business support email
      .replace(/\{\{businessSupportEmail\}\}/g, supportConfig.value?.general?.email)
      // business support phone
      .replace(/\{\{businessSupportPhone\}\}/g, supportConfig.value?.general?.phone)
      // retailer support email
      .replace(/\{\{retailerSupportEmail\}\}/g, supportConfig.value?.retailer?.email)
      // retailer support phone
      .replace(/\{\{retailerSupportPhone\}\}/g, supportConfig.value?.retailer?.phone)
      // full current URL
      .replaceAll('{{currentUrl}}', window.location.href)
      // current route
      .replaceAll('{{currentRoute}}', `${window.location.host}${window.location.pathname}`)
      // user first name
      .replaceAll('{{userFirstName}}', user.value?.firstName || '')
      // user last name
      .replaceAll('{{userLastName}}', user.value?.lastName || '')
      // user full name
      .replaceAll('{{userFullName}}', user.value ? [user.value.firstName, user.value.lastName].filter(Boolean).join(' ') : '')
      // user email
      .replaceAll('{{userEmail}}', user.value?.email || '')
      // market hours
      // .replace(/\{\{marketHours\}\}/g, buildMarketHoursTable(marketHours))
      // market name
      // .replace(/\{\{marketName\}\}/g, userLocale.market?.name || '')
      // market status
      // .replace(/\{\{marketStatus\}\}/g, mapMarketStatus(userLocale.market?.status || ''))
      // pickup hours
      // .replace(/\{\{pickupHours\}\}/g, buildMarketHoursTable(pickupHours))
      // search query
      .replaceAll('{{searchQuery}}', route.query?.q || '')
    // site name
    // .replace(/\{\{siteName\}\}/g, get(siteSettings, 'primary.name', ''))
    // site host
    // .replace(/\{\{siteHost\}\}/g, get(siteSettings, 'primary.host', ''))

    return content
  }

  /**
   * Replace all content tokens and absolute site host URLs with session-specific data
   * @param {string} content The content to replace tokens and URLs in
   * @param {array} findReplace The find/replace strings to apply to content
   * @returns {string} The content with tokens and URLs replaced
   * @see replaceContentTokens
   * @see replaceUrls
   */
  function replaceContentTokensAndUrls(content, findReplace) {
    if (typeof content !== 'string') {
      return content
    }
    content = replaceContentTokens(content, findReplace)
    content = replaceUrls(content)

    return content
  }

  /**
   * Parse all entry content and update accordingly
   * @param {array} entry The entry to replace tokens in
   * @param {array} findReplace The find/replace strings to apply to content
   * @returns {array} The entries with tokens replaced
   */
  function parseEntryContent(entry, findReplace) {
    if (typeof entry !== 'object' || entry === null) {
      return entry
    }

    Object.keys(entry).forEach(key => {
      // ignore non-content keys
      if (
        !!entry[key] &&
        ![
          'type',
          'slug',
          'id',
          'memberGuestDisplay',
          'zipDisplay',
          'themeDisplay',
          'externalSourceOnly',
          'customClasses',
          'classes',
          'buttonType',
          'buttonColor',
          'textColor',
          'alignment',
          'cssStylesMbl',
          'cssStylesDsk',
          'sliderCssStylesMbl',
          'sliderCssStylesDsk',
          'showPagination',
          'fileName',
          'contentType',
          'styles',
          'size',
        ].includes(key)
      ) {
        // map each content element in an array
        if (Array.isArray(entry[key])) {
          entry[key] = entry[key].map(item => parseEntryContent(item, findReplace))
          return
        }

        // map each content element in an object
        if (typeof entry[key] === 'object' && entry[key] !== null && !isEmpty(entry[key])) {
          entry[key] = parseEntryContent(entry[key], findReplace)
          return
        }

        entry[key] = replaceContentTokensAndUrls(entry[key], findReplace)
      }
    })

    return entry
  }

  /**
   * Recursively filter content entries based on user's locale and auth status
   * @param {array} entries The entries to filter
   * @returns {array} The filtered entries
   */
  function deepFilterEntries(entries) {
    const sessionStore = useSessionStore()

    let filteredEntries = entries
      // filter out null/undefined entries
      .filter(Boolean)
      // set contentModules for the current breakpoint
      .map(entry => {
        let { contentModules } = entry.fields
        const { contentModulesMbl: modulesMbl, contentModulesDsk: modulesDsk } = entry.fields

        contentModules = contentModules || modulesMbl || modulesDsk || []

        // set modules by device breakpoint
        if (Screen.xs) {
          // prefer mobile modules, fallback to default
          contentModules = modulesMbl || contentModules || []
        } else {
          // prefer desktop modules, fallback to default
          contentModules = modulesDsk || contentModules || []
        }
        delete entry.fields.contentModulesMbl
        delete entry.fields.contentModulesDsk

        entry.fields.contentModules = contentModules

        return entry
      })

    // client-only filtering
    if (process.env.CLIENT) {
      // filter the entries
      filteredEntries = filteredEntries.filter(entry => {
        const { contentModules, merchRows } = entry.fields
        let { externalSourceOnly = false } = entry.fields
        const filterTags = entry.metadata.tags.reduce((acc, { sys }) => {
          if (sys?.id?.startsWith('filter')) {
            acc.push(sys.id)
          }
          return acc
        }, [])

        let isEligible = true

        // device
        const phoneOnly = filterTags.includes('filterDevicePhoneOnly')
        const tabletPlusOnly = filterTags.includes('filterDeviceTabletPlusOnly')

        if (phoneOnly) {
          isEligible = isEligible && Screen.xs
        } else if (tabletPlusOnly) {
          isEligible = isEligible && Screen.gt.xs
        }

        // source
        externalSourceOnly = filterTags.includes('filterSourceExternalOnly') || externalSourceOnly
        if (externalSourceOnly) {
          const isExtSrcReferred = Boolean(route.query.ext_src && route.query.ext_src.trim().length > 0)
          isEligible = isEligible && isExtSrcReferred
        }

        // member/guest user
        const membersOnly = filterTags.includes('filterUserMemberOnly') // || /member/i.test(memberGuestDisplay)
        const guestsOnly = filterTags.includes('filterUserGuestOnly') // || /guest/i.test(memberGuestDisplay)
        if (membersOnly) {
          isEligible = isEligible && sessionStore.sessionIsAuthed
        } else if (guestsOnly) {
          isEligible = isEligible && !sessionStore.sessionIsAuthed
        }

        // recursivley parse childe contentMoudles as well
        if (isEligible) {
          // parse child modules
          if (contentModules?.length) {
            entry.fields.contentModules = deepParseEntries(contentModules)
          }
          // parse child merchRows
          if (merchRows?.length) {
            entry.fields.merchRows = deepParseEntries(merchRows)
          }
        }

        return isEligible
      })
    }

    return filteredEntries
  }

  /**
   * Recursively parse content entries
   * @param {array} entries The entries to parse
   * @returns {array} The parsed entries
   */
  function deepParseEntries(entries = []) {
    // filter out invalid entries first
    let parsedEntries = entries.filter(entry => !!entry.fields && !!entry?.sys?.contentType)

    // filter entries
    parsedEntries = deepFilterEntries(parsedEntries)

    return parsedEntries
  }

  /**
   * Get the mapped sluge for a given route path and query params
   * @returns {string} The slug for the given route
   */
  function getPageSlugByRoute() {
    let slug = null

    switch (route.path) {
      case '/':
      case '/app':
      case '/app/':
        slug = 'home-page'
        break
    }

    return slug
  }

  /**
   * Recursively mutate and remove null properties from an object
   * @param {object} entry The entry to remove null properties from
   */
  function removeNullProperties(entry) {
    Object.keys(entry).forEach(key => {
      if (entry[key] === null) {
        delete entry[key]
      }
    })

    if (entry?.contentModules?.length) {
      entry.contentModules.forEach(mod => removeNullProperties(mod))
    }
  }

  /**
   * Parse and filter the response entry into a usable format
   * @param {object} rawEntry The raw entry from Contentful
   * @returns {object} The parsed entry
   */
  function parseRawEntry(rawEntry) {
    // recursively remove all null entry properties (mainly becuase live-preview introduces null properties)
    removeNullProperties(rawEntry)

    return parseResponseEntry({ entry: rawEntry })
  }

  /**
   * Perform a Contentful API request to fetch the requested entry
   * @param {object} query The query to fetch the entry with
   * @param {object} options Additional options for fetching the entry
   * @returns {object} The fetched entry
   */
  async function fetchEntry(query, options = {}) {
    const { raw = false, findReplace = [] } = options

    try {
      // fetch the entry
      const response = await client.getEntries(query)

      // recursively filter all entries
      response.items = deepParseEntries(response.items)

      const rawEntry = get(response, 'items[0]')

      // $logger.debug('CONTENTFUL GET ENTRY (RAW)', rawEntry)

      if (!rawEntry) {
        $logger.error('CONTENTFUL RAW ENTRY UNDEFINED', query)

        return null
      }

      // return raw response (for live-preview mode)
      if (raw) {
        $logger.debug('CONTENTFUL GET ENTRY (RAW)', rawEntry)
        return rawEntry
      }

      // parse and filter entry
      const parsedEntry = parseEntryContent(parseRawEntry(rawEntry), findReplace)
      $logger.debug('CONTENTFUL GET ENTRY', parsedEntry)

      // apply code highlighting
      $prism.highlightAll()

      return parsedEntry
    } catch (error) {
      $logger.error('CONTENTFUL GET ENTRY FAILED', query)
      $logger.error(error)
      if (error && !isEmpty(error) && error.code !== 'ERR_NETWORK') {
        $rollbar.error('Error fetching contentful entry', { query, error })
      }
    }

    return null
  }

  /**
   * Get the requested contentful entry
   * @param {object} params
   * @param {string} params.type Entity type to retrieve
   * @param {string} params.slug Entity slug
   * @param {array} params.tags Filter entities by specified tags
   * @param {number} params.include Depth to include nested entities
   * @param {array} options.findReplace Array of find/replace strings to apply to content
   * @returns {object} The requested entry
   */
  async function getEntry(params, options = {}) {
    // const { siteMeta, userLocale } = ctx.store.state
    // const { isFlagshipSite, isNicheSite, isWhiteLabelSite } = ctx.store.getters

    // // if user locale is empty, keep retrying every 200ms until it's available
    // if (process.env.CLIENT && isEmpty(userLocale)) {
    //   $logger.debug('User locale is empty, retry in 200ms', params)
    //   await $async.delay(200)
    //   return getEntry(params, options)
    // }

    const { type = 'page' } = params
    // check for page mapping overrides based on route
    // BYPASS IF IN MAINTENANCE MODE to ensure provided slug is used
    if (/page/i.test(type)) {
      params.slug = getPageSlugByRoute() || params.slug
    }

    const { id, slug, include = 10 } = params
    const query = { content_type: type, include }
    // select: 'sys.id,fields.header,fields.contentModules',

    // invalid slug
    if (!slug || slug === 'undefined' || slug.length < 3 || /[^\w-]/.test(slug)) {
      $logger.error('CONTENTFUL GET ENTRY INVALID SLUG', slug)
      // redirect('/404')
      return {}
    }

    if (slug) {
      query['fields.slug'] = slug
    } else if (id) {
      query['sys.id'] = id
    } else {
      $logger.error('CONTENTFUL GET ENTRY INVALID ID/SLUG', params)
      // redirect('/404')
      return {}
    }

    const entry = await fetchEntry(query, options)

    if (!entry) {
      $logger.error('CONTENTFUL GET ENTRY FAILED', query)
      // redirect('/404')
    }

    return entry
  }

  /**
   * Get the Contentful asset by ID
   * @param {string} assetId The ID of the asset to retrieve
   * @returns {object} The asset returned from Contentful
   */
  async function getAsset(assetId) {
    let asset = { url: '' }

    try {
      // const { fields } = await client.getAsset(assetId)
      const { fields } = await client.getAsset(assetId)

      if (fields?.file?.url) {
        asset = { alt: fields.description || '', src: fields.file.url }
      }
    } catch (error) {
      $logger.error('CONTENTFUL GET ASSET FAILED', assetId)
      if (error && !isEmpty(error)) {
        $rollbar.error('Contentful asset error', { assetId, error })
      }
    }

    if (process.env.CLIENT) {
      $logger.debug('CONTENTFUL GET ASSET', asset)
    }

    return asset
  }

  $contentful = {
    getEntry,
    getAsset,
    replaceContentTokens,
  }
})
